import React, { ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { captureException } from '@sentry/react'
import { UserAvatar } from '../../../common/Avatar'
import { theme } from '../../../../theme/theme'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { useModal } from '../../../../hooks/useModal'
import { CreateUpdateBlockedUserModal } from '../CreateUpdateBlockedUserModal'
import { Dropdown } from '../../../common/Dropdown'
import { Button, ButtonVariant } from '../../../common/Button'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { IconChoices } from '../../../common/Icon'
import { ConfirmationModal } from '../../../common/ConfirmationModal'
import { userQueries } from '../../../../api/users'
import { getUserFullName } from '../../../../helpers/user'
import { removeBlockedUser } from '../../../../api/societies'
import {
  chatMemberCandidatesQueryKey,
  chatRecipientsQueryKey,
} from '../../../../api/chat'

interface BlockedUserListItemProps {
  userId: string
}

export const BlockedUserListItem = ({
  userId,
}: BlockedUserListItemProps): ReactNode => {
  const { society } = useCurrentSociety()
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const queryClient = useQueryClient()
  const { show: showEditModal, close: closeEditModal } = useModal()
  const {
    show: showDeleteBlockConfirmationModal,
    close: closeDeleteBlockConfirmationModal,
    open: openDeleteBlockConfirmationModal,
  } = useModal()

  const { data: user } = useQuery(userQueries.getUser(userId))

  const { mutate: mutateRemoveBlockedUser } = useMutation({
    mutationFn: async (params: { societyId: string; userId: string }) => {
      return (await removeBlockedUser(params.societyId, params.userId)).data
        .data
    },
    onError: (error) => {
      captureException(error)
      setToastNotification(
        ToastType.DANGER,
        translate('societyBlockedUsersView.flashMessage.userBlockRemoveFailure')
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatRecipientsQueryKey })
      queryClient.invalidateQueries({ queryKey: chatMemberCandidatesQueryKey })
      setToastNotification(
        ToastType.SUCCESS,
        translate('societyBlockedUsersView.flashMessage.userBlockRemoveSuccess')
      )
    },
  })

  if (!user) {
    return null
  }

  const onRemoveBlockedUser = async (): Promise<void> => {
    if (!society) return
    mutateRemoveBlockedUser({ societyId: society?._id, userId: user._id })
  }

  const dropdownOptions = [
    {
      value: 'remove',
      label: translate('common.actions.remove'),
      onClick: openDeleteBlockConfirmationModal,
      destructive: true,
    },
  ]

  const fullName = getUserFullName(user)

  return (
    <div className="flex flex-row items-center border-b pb-4">
      <div className="flex flex-10 flex-row items-center">
        <UserAvatar user={user} />
        <div className="ml-4 flex flex-col justify-start">
          <p
            className="text-neutral-30"
            style={theme.textVariants.base}
            translate="no"
          >
            {fullName}
          </p>
        </div>
      </div>

      <Dropdown options={dropdownOptions} className="mr-3">
        <Button
          variant={ButtonVariant.TEXT}
          className="h-10 w-10 border-none bg-transparent hover:bg-neutral-90"
          icon={IconChoices.MORE}
          disableTabIndex
        />
      </Dropdown>

      {society && (
        <CreateUpdateBlockedUserModal
          society={society}
          show={showEditModal}
          close={closeEditModal}
        />
      )}
      <ConfirmationModal
        title={translate('societyBlockedUsersView.alert.deleteBlockTitle')}
        description={translate('societyBlockedUsersView.alert.deleteBlockText')}
        show={showDeleteBlockConfirmationModal}
        close={closeDeleteBlockConfirmationModal}
        onConfirm={onRemoveBlockedUser}
      />
    </div>
  )
}
